import React, {useEffect, useRef, useState} from "react";
import {renderStrokes} from "../games/dancelestrations/strokeRenderer";

const parseRecording = (raw) => {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.events)) return parsed;
    } catch (e) {}
    return null;
};

const EndingBackdrop = () => (
    <div style={{
        position: 'fixed',
        inset: 0,
        backgroundImage: 'linear-gradient(#000000 35%, #c13000 100%)',
        zIndex: -1,
        pointerEvents: 'none',
    }}/>
);

const cardStyle = {
    width: '100%',
    maxWidth: 360,
    boxSizing: 'border-box',
    margin: '0 auto 36px',
    padding: '8px 0',
    color: 'white',
};

const sectionTitleStyle = {
    fontSize: 14,
    fontWeight: 800,
    fontStyle: 'italic',
    letterSpacing: 4,
    textTransform: 'uppercase',
    opacity: 0.7,
    textAlign: 'center',
    marginBottom: 12,
};

const replayBtnStyle = {
    marginTop: 8,
    padding: '8px 18px',
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: 3,
    textTransform: 'uppercase',
    fontFamily: 'inherit',
    color: 'white',
    background: 'linear-gradient(180deg, #2a2a32 0%, #0a0a14 100%)',
    border: '1px solid rgba(255,255,255,0.55)',
    borderRadius: 6,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), 0 2px 0 rgba(0,0,0,0.6)',
    cursor: 'pointer',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
};

const ReplayCanvas = ({recording, label}) => {
    const canvasRef = useRef(null);
    const cancelRef = useRef(null);

    const renderSnap = () => {
        if (!canvasRef.current || !recording) return;
        if (cancelRef.current) { cancelRef.current(); cancelRef.current = null; }
        renderStrokes(canvasRef.current, recording, {animated: false});
    };

    const renderAnimated = () => {
        if (!canvasRef.current || !recording) return;
        if (cancelRef.current) { cancelRef.current(); cancelRef.current = null; }
        const handle = renderStrokes(canvasRef.current, recording, {animated: true, targetMs: 4000});
        cancelRef.current = handle.cancel;
    };

    useEffect(() => {
        renderSnap();
        return () => { if (cancelRef.current) cancelRef.current(); };
    }, [recording]);

    return <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6}}>
        {label && <div style={{
            fontSize: 20,
            fontWeight: 700,
            fontStyle: 'italic',
            opacity: 0.85,
            textShadow: '1px 1px 0 rgba(0,0,0,0.9)',
        }}>"{label}"</div>}
        <canvas
            ref={canvasRef}
            width={500}
            height={500}
            style={{
                width: '100%',
                aspectRatio: '1 / 1',
                height: 'auto',
                backgroundColor: '#0a0a0a',
                display: 'block',
            }}
        />
        {recording && <button onClick={renderAnimated} style={replayBtnStyle}>▶ Replay</button>}
        {!recording && <div style={{fontSize: 12, opacity: 0.5, fontStyle: 'italic'}}>no drawing</div>}
    </div>;
};

export const Ending = ({name, isControl}) => {
    const [savedCharacter, setSavedCharacter] = useState();
    const [dancelestrationsSummary, setDancelestrationsSummary] = useState();
    const [trilemmaScore, setTrilemmaScore] = useState();
    const [allegedlyTrueScore, setAllegedlyTrueScore] = useState();
    const [leaderboardEntry, setLeaderboardEntry] = useState();

    useEffect(() => {
        if (isControl) return;

        fetch('/users/current', {credentials: 'include'})
            .then(r => r.ok ? r.json() : null)
            .then(u => {
                if (!u || !u.token) return;
                fetch('/leaderboard/get-leaderboard?type=end&limit=all')
                    .then(r => r.json())
                    .then(rows => {
                        const me = (rows || []).find(row => row.character && row.character.token === u.token);
                        if (me) setLeaderboardEntry(me);
                    })
                    .catch(() => {});
                fetch('/trilemma/get-scores')
                    .then(r => r.json())
                    .then(rows => {
                        const me = (rows || []).find(row => row.character && row.character.token === u.token);
                        if (me) setTrilemmaScore(me);
                    })
                    .catch(() => {});
                fetch('/allegedly-true/get-scores')
                    .then(r => r.json())
                    .then(rows => {
                        const me = (rows || []).find(row => row.character && row.character.token === u.token);
                        if (me) setAllegedlyTrueScore(me);
                    })
                    .catch(() => {});
            });

        fetch('/character-creator/get-character', {credentials: 'include'})
            .then(response => response.json())
            .then(setSavedCharacter);

        fetch('/dancelestrations/get-personal-summary', {credentials: 'include'})
            .then(response => response.json())
            .then(setDancelestrationsSummary);
    }, [isControl]);

    if (isControl) return null;
    if (!savedCharacter || !dancelestrationsSummary) return null;

    const initialRecording = parseRecording(dancelestrationsSummary.initial_drawing);
    const danceRecording = parseRecording(dancelestrationsSummary.dance_guess_drawing);

    return <div style={{
        alignSelf: 'flex-start',
        width: '100%',
        maxWidth: '100vw',
        minHeight: '100dvh',
        boxSizing: 'border-box',
        padding: 'calc(24px + env(safe-area-inset-top)) 16px calc(40px + env(safe-area-inset-bottom))',
        color: 'white',
        textAlign: 'center',
        overflowX: 'hidden',
    }}>
        <EndingBackdrop />

        <div style={{maxWidth: 360, margin: '0 auto 20px'}}>
            <img
                src="/party-machine-logo.png"
                alt="Party Machine"
                style={{
                    width: '100%',
                    maxWidth: 320,
                    display: 'block',
                    margin: '0 auto',
                    filter: 'drop-shadow(2px 2px 0 rgba(0,0,0,0.7))',
                }}
            />
        </div>

        <div style={cardStyle}>
            {leaderboardEntry && (
                <div style={{
                    fontSize: 72,
                    fontWeight: 900,
                    fontStyle: 'italic',
                    lineHeight: 1,
                    color: '#f1d28b',
                    textShadow: '0 0 22px rgba(241,210,139,0.6), 3px 3px 0 rgba(0,0,0,0.95)',
                    marginBottom: 8,
                }}>#{leaderboardEntry.rank}</div>
            )}
            <img
                src={savedCharacter.image_url}
                alt={savedCharacter.nickname}
                style={{
                    width: '80%',
                    objectFit: 'cover',
                    objectPosition: 'top',
                    filter: 'drop-shadow(0 0 1px black) drop-shadow(0 0 1px black) drop-shadow(0 0 1px black)',
                }}
            />
            <div style={{
                fontSize: 26,
                fontWeight: 800,
                marginTop: 8,
                textShadow: '2px 2px 0 rgba(0,0,0,0.9)',
            }}>{name}</div>
            <div style={{
                fontSize: 20,
                fontStyle: 'italic',
                opacity: 0.85,
                marginTop: 2,
                textShadow: '1px 1px 0 rgba(0,0,0,0.9)',
            }}>{savedCharacter.nickname}</div>
        </div>

        <div style={cardStyle}>
            <img
                src="/dancelestrations-assets/logo.png"
                alt="Dancelestrations"
                style={{
                    width: '90%',
                    marginBottom: 14,
                    filter: 'brightness(0) invert(1) drop-shadow(2px 2px 0 rgba(0,0,0,0.7))',
                }}
            />
            {dancelestrationsSummary.dancing_image_url ? (
                <img
                    src={dancelestrationsSummary.dancing_image_url}
                    style={{
                        width: '100%',
                        maxWidth: 280,
                        objectFit: 'contain',
                        borderRadius: 6,
                        marginBottom: 16,
                    }}
                />
            ) : (
                <div style={{fontSize: 13, opacity: 0.5, fontStyle: 'italic', marginBottom: 4}}></div>
            )}
            <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
                <ReplayCanvas
                    recording={initialRecording}
                    label={dancelestrationsSummary.initial_word}
                />
                <ReplayCanvas
                    recording={danceRecording}
                    label={dancelestrationsSummary.dance_guess_word || 'anything'}
                />
            </div>
        </div>

        <div style={cardStyle}>
            <img
                src="/trilemma-assets/logo.webp"
                alt="Trilemma"
                style={{width: '90%', marginBottom: 8, filter: 'drop-shadow(2px 2px 0 rgba(0,0,0,0.7))'}}
            />
            {trilemmaScore ? (
                <div style={{fontSize: 30, fontWeight: 800}}>
                    <span style={{color: '#ffad00'}}>{trilemmaScore.score} pts</span>
                    <span style={{opacity: 0.8}}> · Rank #{trilemmaScore.rank}</span>
                </div>
            ) : (
                <div style={{fontSize: 13, opacity: 0.5}}>—</div>
            )}
        </div>

        <div style={cardStyle}>
            <div style={{
                fontSize: 52,
                fontWeight: 900,
                fontStyle: 'italic',
                letterSpacing: 2,
                textTransform: 'uppercase',
                marginBottom: 14,
                textShadow: '0 2px 0 rgba(0,0,0,0.85), 0 0 18px rgba(255,255,255,0.18)',
            }}>Allegedly<br />True</div>
            {allegedlyTrueScore ? (
                <div style={{fontSize: 30, fontWeight: 800}}>
                    <span style={{color: '#fff'}}>{allegedlyTrueScore.score} pts</span>
                    <span style={{opacity: 0.8}}> · Rank #{allegedlyTrueScore.rank}</span>
                </div>
            ) : (
                <div style={{fontSize: 13, opacity: 0.5}}>—</div>
            )}
        </div>

        <div style={{marginTop: 20, fontSize: 12, opacity: 0.5, fontStyle: 'italic'}}>
            🎉 Thanks for Playing 🎉
        </div>
    </div>;
}
